import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { aoRestaurar, gravarBotoes, lerBotoes, type CasaBotao } from './posicoes';
import { useModo, type ModoTela } from './useModoTela';
import { cursorArraste } from '../../input/cursor';

/**
 * Botões flutuantes do HUD V3 (techspec-hud-v3 §06): cada um mora numa casa de uma grade ancorada no
 * canto de baixo à direita do mundo. Arrastar mais de 6 px move o botão e mostra as casas livres; soltar
 * encaixa na casa livre mais perto. Não vale casa que cobre o topo, a barra, o chat, a navegação do
 * celular, o minimapa ou outro botão. A casa fica guardada no navegador (posicoes.ts).
 *
 * §23: a placa de vida entra na mesma grade. Ela é um item de duas colunas (`colunas`), não é botão e
 * arrasta por qualquer ponto; nasce no canto de cima à esquerda.
 *
 * techspec-correio §06: a carta flutuante é mais um item da grade. Só aparece com carta nova, nasce embaixo da placa
 * de vida e arrasta e guarda a casa como os botões.
 */
export type IdBotao = 'bolsa' | 'combate' | 'viajar' | 'recursos' | 'amigos' | 'vida' | 'alvo' | 'correio';

export interface BotaoFlutuante {
  id: IdBotao;
  rotulo: string;
  /** O texto curto embaixo do botão (techspec-hud-v3 §18): o ícone sozinho não dizia o que faz. */
  legenda?: string;
  dica?: string;
  ligado?: boolean;
  conteudo: ReactNode;
  onClick?(): void;
  /** Número no canto (mensagens não lidas). */
  pip?: number;
  /** Placa (§23): ocupa `colunas` casas lado a lado, sem legenda, e não é botão. */
  colunas?: number;
  /** Classe a mais na placa (a do alvo tem a borda vermelha) ou no botão (a carta do correio). */
  classe?: string;
}

/** Onde cada item está, em px dentro do mundo: as janelas Viajar e Combate abrem ao lado. */
export type PosicoesBotoes = Partial<Record<IdBotao, { x: number; y: number; s: number; w: number }>> & { mundo?: [number, number] };

/** A casa padrão, ou um ponto do mundo (a placa nasce na casa livre mais perto dele). */
type Padrao = CasaBotao | { px: [number, number] };

/** `g` é o passo na horizontal; `gy`, na vertical, tem a folga da legenda (`l`, a altura dela embaixo do botão). */
type Grade = { s: number; g: number; gy: number; l: number; m: number; pad: Record<IdBotao, Padrao> };
const grade = (modo: ModoTela): Grade => modo === 'cel' || modo === 'deitado'
  ? { s: 48, g: 60, gy: 72, l: 18, m: 10, pad: { bolsa: [0, 3], combate: [0, 4], viajar: [0, 5], recursos: [0, 6], amigos: [1, 3], vida: { px: [10, 60] }, alvo: { px: [10, 140] }, correio: { px: [10, 190] } } }
  // Renato, 26/10: um pouco menores no computador (eram 56 px, passo 70 × 82).
  : { s: 48, g: 60, gy: 74, l: 20, m: 18, pad: { bolsa: [0, 0], combate: [0, 1], viajar: [0, 2], recursos: [0, 3], amigos: [0, 4], vida: { px: [14, 70] }, alvo: { px: [14, 160] }, correio: { px: [14, 230] } } };

/**
 * A grade de encaixe tem meio passo (`g / SUB` e `gy / SUB`): o jogador posiciona mais perto do que quer. O tamanho
 * dos itens continua o mesmo (`g` ainda mede a largura das placas); a colisão passa a ser por retângulo.
 */
const SUB = 2;
/** Folga mínima entre dois itens encaixados (px). */
const FOLGA = 4;

/** O que o botão não pode cobrir. */
const ZONAS = ['.topbar', '.skillbar', '.p-navcel', '.p-chat__linha', '.win.p-chat:not(.p-chat--folha)', '.win--minimap'];
type Ret = [number, number, number, number];
/** Uma casa ocupada: coluna da direita, linha e quantas colunas o item cobre para a esquerda. */
type Ocupada = { c: number; r: number; k: number };

function zonas(mundo: HTMLElement): Ret[] {
  const p = mundo.getBoundingClientRect();
  return ZONAS.flatMap(sel => [...mundo.querySelectorAll<HTMLElement>(sel)])
    .filter(el => !el.hidden && !el.closest('[hidden]'))
    .map(el => el.getBoundingClientRect())
    .filter(r => r.width && r.height)
    .map(r => [r.left - p.left, r.top - p.top, r.right - p.left, r.bottom - p.top] as Ret);
}

export function BotoesFlutuantes({ botoes, onPosicoes }: { botoes: readonly BotaoFlutuante[]; onPosicoes?(p: PosicoesBotoes): void }) {
  const modo = useModo();
  const G = grade(modo);
  const host = useRef<HTMLDivElement>(null);
  const [tam, setTam] = useState<[number, number]>([0, 0]);
  const [salvo, setSalvo] = useState<Record<string, CasaBotao>>(() => lerBotoes(modo));
  const [zonasAgora, setZonas] = useState<Ret[]>([]);
  const [arrasto, setArrasto] = useState<{ id: IdBotao; x: number; y: number; alvo: CasaBotao } | null>(null);
  const segura = useRef<{ id: IdBotao; x0: number; y0: number; l0: number; t0: number; ativo: boolean; pid: number } | null>(null);
  const engoliuClique = useRef(false);
  const [solto, setSolto] = useState<IdBotao | null>(null);

  useEffect(() => { setSalvo(lerBotoes(modo)); }, [modo]);
  useEffect(() => aoRestaurar(() => setSalvo({})), []);

  /* o tamanho do mundo e as zonas proibidas: medem de novo quando a tela muda e de tempos em tempos
     (o chat abre e fecha, o minimapa aparece) */
  useLayoutEffect(() => {
    const mundo = host.current?.parentElement;
    if (!mundo) return;
    const medir = () => {
      setTam(t => (t[0] === mundo.clientWidth && t[1] === mundo.clientHeight ? t : [mundo.clientWidth, mundo.clientHeight]));
      const z = zonas(mundo);
      setZonas(a => (JSON.stringify(a) === JSON.stringify(z) ? a : z));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(mundo);
    const id = window.setInterval(medir, 1500);
    return () => { ro.disconnect(); window.clearInterval(id); };
  }, []);

  const [W, H] = tam;
  const colunasDe = (id: IdBotao) => Math.max(1, botoes.find(b => b.id === id)?.colunas ?? 1);
  /** Largura de um item de `k` colunas: a casa da direita mais os passos até a da esquerda. */
  const largura = useCallback((k: number) => G.s + (k - 1) * G.g, [G]);
  /** O canto de cima à esquerda do item cuja casa da direita é `c`. */
  const xyCasa = useCallback(([c, r]: CasaBotao, k = 1): [number, number] =>
    [W - G.m - G.s - c * (G.g / SUB) - (k - 1) * G.g, H - G.m - G.l - G.s - r * (G.gy / SUB)], [W, H, G]);
  const livre = useCallback((c: CasaBotao, k: number, ocupadas: Ocupada[]) => {
    if (c[0] - (k - 1) * SUB < 0) return false;
    const [x, y] = xyCasa(c, k);
    if (x < G.m - 1 || y < G.m - 1) return false;
    const q = [x - 4, y - 4, x + largura(k) + 4, y + G.s + G.l + 4];
    if (zonasAgora.some(z => q[0] < z[2] && q[2] > z[0] && q[1] < z[3] && q[3] > z[1])) return false;
    /* com meio passo, um item cobre mais de uma casa: a conta é pelo retângulo (botão com a legenda; a placa cresce
       para a esquerda, como em xyCasa), com uma folga pequena entre os dois. */
    const a = [x, y, x + largura(k), y + G.s + G.l];
    return !ocupadas.some(o => {
      const [ox, oy] = xyCasa([o.c, o.r], o.k);
      const b = [ox, oy, ox + largura(o.k), oy + G.s + G.l];
      return a[0] < b[2] + FOLGA && a[2] + FOLGA > b[0] && a[1] < b[3] + FOLGA && a[3] + FOLGA > b[1];
    });
  }, [xyCasa, largura, zonasAgora, G]);
  const casas = useCallback(() => {
    const out: CasaBotao[] = [];
    if (!W || !H) return out;
    for (let c = 0; c <= Math.floor((W - G.m - G.s) / (G.g / SUB)); c++) {
      for (let r = 0; r <= Math.floor((H - G.m - G.l - G.s) / (G.gy / SUB)); r++) out.push([c, r]);
    }
    return out;
  }, [W, H, G]);
  /** A casa livre cujo centro do item fica mais perto de (cx, cy). */
  const maisPerto = useCallback((cx: number, cy: number, k: number, usadas: Ocupada[]) => {
    let melhor: CasaBotao | null = null, dist = Infinity;
    for (const c of casas()) {
      if (!livre(c, k, usadas)) continue;
      const [x, y] = xyCasa(c, k);
      const d = Math.hypot(x + largura(k) / 2 - cx, y + G.s / 2 - cy);
      if (d < dist) { dist = d; melhor = c; }
    }
    return melhor;
  }, [casas, livre, xyCasa, largura, G]);

  /* a casa de cada item: a guardada ou a padrão; se não serve (a tela mudou), a livre mais perto.
     Os botões entram antes da placa: a placa grande acha lugar com mais facilidade. */
  const pos: Partial<Record<IdBotao, CasaBotao>> = {};
  const usadas: Ocupada[] = [];
  const ordem = [...botoes].sort((a, b) => (a.colunas ?? 1) - (b.colunas ?? 1));
  for (const b of ordem) {
    const k = Math.max(1, b.colunas ?? 1);
    const pad = G.pad[b.id];
    let c: CasaBotao | null = salvo[b.id] ?? (Array.isArray(pad) ? [pad[0] * SUB, pad[1] * SUB] : null);
    if (W && (!c || !livre(c, k, usadas))) {
      const [cx, cy] = c
        ? (() => { const [x, y] = xyCasa(c, k); return [x + largura(k) / 2, y + G.s / 2]; })()
        : [(pad as { px: [number, number] }).px[0] + largura(k) / 2, (pad as { px: [number, number] }).px[1] + G.s / 2];
      c = maisPerto(cx, cy, k, usadas) ?? c;
    }
    if (!c) continue;
    pos[b.id] = c;
    usadas.push({ c: c[0], r: c[1], k });
  }
  const chavePos = JSON.stringify(pos) + W + 'x' + H;
  const posRef = useRef(pos);
  posRef.current = pos;
  const avisar = useRef(onPosicoes);
  avisar.current = onPosicoes;
  useEffect(() => {
    if (!W || !avisar.current) return;
    const out: PosicoesBotoes = { mundo: [W, H] };
    for (const [id, c] of Object.entries(posRef.current) as [IdBotao, CasaBotao][]) {
      const k = colunasDe(id);
      const [x, y] = xyCasa(c, k);
      out[id] = { x, y, s: G.s, w: largura(k) };
    }
    avisar.current(out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chavePos]);

  const outras = (id: IdBotao): Ocupada[] => (Object.entries(pos) as [IdBotao, CasaBotao][])
    .filter(([k]) => k !== id).map(([k, v]) => ({ c: v[0], r: v[1], k: colunasDe(k) }));

  const down = (e: React.PointerEvent<HTMLElement>, id: IdBotao) => {
    if (e.button !== 0 || !pos[id]) return;
    const [l0, t0] = xyCasa(pos[id]!, colunasDe(id));
    segura.current = { id, x0: e.clientX, y0: e.clientY, l0, t0, ativo: false, pid: e.pointerId };
    engoliuClique.current = false;
    /* captura já no toque: um primeiro movimento rápido sairia do item antes dos 6 px e o arrasto não começava */
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ponteiro já solto */ }
  };
  const move = (e: React.PointerEvent<HTMLElement>) => {
    const a = segura.current;
    if (!a || a.pid !== e.pointerId) return;
    const dx = e.clientX - a.x0, dy = e.clientY - a.y0;
    if (!a.ativo && Math.hypot(dx, dy) < 6) return;
    if (!a.ativo) {
      a.ativo = true;
      cursorArraste('janela', true);
    }
    const k = colunasDe(a.id);
    const x = a.l0 + dx, y = a.t0 + dy;
    const alvo = maisPerto(x + largura(k) / 2, y + G.s / 2, k, outras(a.id)) ?? pos[a.id]!;
    setArrasto({ id: a.id, x, y, alvo });
  };
  const up = (e: React.PointerEvent<HTMLElement>) => {
    const a = segura.current;
    segura.current = null;
    if (!a || a.pid !== e.pointerId || !a.ativo) return;
    engoliuClique.current = true;
    cursorArraste('janela', false);
    const k = colunasDe(a.id);
    const x = a.l0 + (e.clientX - a.x0), y = a.t0 + (e.clientY - a.y0);
    const casa = maisPerto(x + largura(k) / 2, y + G.s / 2, k, outras(a.id)) ?? pos[a.id]!;
    const novo = { ...(pos as Record<string, CasaBotao>), [a.id]: casa };
    gravarBotoes(modo, novo);
    setSalvo(novo);
    setArrasto(null);
    setSolto(a.id);
  };
  const cancelar = () => { segura.current = null; setArrasto(null); cursorArraste('janela', false); };

  const kArrasto = arrasto ? colunasDe(arrasto.id) : 1;

  return (
    <div className="v3-fab" ref={host}>
      {arrasto && (
        <div className="v3-fab__grade" aria-hidden="true">
          {casas().filter(c => livre(c, kArrasto, outras(arrasto.id))).map(c => {
            const [x, y] = xyCasa(c, kArrasto);
            return <i key={`${c[0]}-${c[1]}`} style={{ left: x + largura(kArrasto) / 2, top: y + G.s / 2 }} />;
          })}
          {(() => {
            const [ax, ay] = xyCasa(arrasto.alvo, kArrasto);
            return <b className={kArrasto > 1 ? 'is-placa' : undefined} style={{ left: ax, top: ay, width: largura(kArrasto), height: G.s }} />;
          })()}
        </div>
      )}
      {W > 0 && botoes.map(b => {
        if (!pos[b.id]) return null;
        const k = Math.max(1, b.colunas ?? 1);
        const [x, y] = arrasto?.id === b.id ? [arrasto.x, arrasto.y] : xyCasa(pos[b.id]!, k);
        const estado = `${b.ligado ? ' is-on' : ''}${arrasto?.id === b.id ? ' is-arrastando' : ''}${solto === b.id ? ' is-solto' : ''}`;
        const eventos = {
          onPointerDown: (e: React.PointerEvent<HTMLElement>) => down(e, b.id),
          onPointerMove: move,
          onPointerUp: up,
          onPointerCancel: cancelar
        };
        if (k > 1) {
          return (
            <div key={b.id} className={`v3-fab__placa${b.classe ? ` ${b.classe}` : ''}${estado}`} data-fab={b.id} role="group" aria-label={b.rotulo} title={b.dica}
              style={{ left: x, top: y, width: largura(k) }} {...eventos}>
              {b.conteudo}
            </div>
          );
        }
        return (
          <button
            key={b.id}
            type="button"
            className={`v3-fab__b${b.classe ? ` ${b.classe}` : ''}${estado}`}
            data-fab={b.id}
            aria-pressed={b.ligado}
            aria-label={b.pip ? `${b.rotulo} · ${b.pip}` : b.rotulo}
            title={b.dica}
            style={{ left: x, top: y }}
            {...eventos}
            onClick={() => {
              if (engoliuClique.current) { engoliuClique.current = false; return; }
              b.onClick?.();
            }}
          >
            {b.conteudo}
            {!!b.pip && <span className="v3-fab__pip" aria-hidden="true">{b.pip > 99 ? '99+' : b.pip}</span>}
            {b.legenda && <span className="v3-fab__leg" aria-hidden="true">{b.legenda}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Ao lado do botão, onde ele estiver: à esquerda se o botão está na metade direita do mundo. */
export function ancoraAoLado(pos: PosicoesBotoes, id: IdBotao, barraH = 92) {
  const p = pos[id];
  const [W, H] = pos.mundo ?? [0, 0];
  if (!p || !W) return { right: 88, bottom: barraH + 10 };
  const bottom = Math.max(H - (p.y + p.s), barraH + 10);
  return p.x + p.w / 2 > W / 2 ? { right: W - p.x + 14, bottom } : { left: p.x + p.w + 14, bottom };
}
